import { useRouter } from 'next/router';
import { type MouseEvent, useRef } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { IconName } from '@/components/Icon/Icon';
import { isInternalLink } from '@/contentful/contentful-exports';
import { trackEvents } from '@/utils/analytics';

import useStyles from './FeatureLink.styles';

type FeatureLinkProps = {
  category: string;
  title: string;
  buttonLabel: string;
  isOnDark: boolean;
  imageUrl?: string;
  url?: string;
  variant?: ButtonVariant;
};

const FeatureLink = ({
  category,
  title,
  imageUrl,
  buttonLabel,
  isOnDark,
  url,
  variant = ButtonVariant.SECONDARY,
}: FeatureLinkProps) => {
  const router = useRouter();
  const { classes, cx } = useStyles({ isOnDark });
  const anchor = useRef<HTMLAnchorElement>(null);

  // Using anchor.current.href value for navigation to allow ABTasty to override urls
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const target = anchor.current?.getAttribute('href') || url;

    trackEvents({
      event: 'engagement',
      id: `feature-link-${title}`,
      banner_name: title,
      nav: category,
    });

    if (!target) return;

    const isTargetInternal = isInternalLink(target);

    // Allow default anchor behavior for external links or modifier keys
    if (!isTargetInternal || e.metaKey || e.ctrlKey || e.shiftKey) {
      return;
    }

    e.preventDefault();
    router.push(target);
  };

  const handleMouseEnter = () => {
    const target = anchor.current?.getAttribute('href') || url;
    if (target && isInternalLink(target)) {
      router.prefetch(target);
    }
  };

  return (
    <div className={cx(classes.card, classes[variant])}>
      <div
        data-testid='background'
        className={classes.cardBackgroundImg}
        style={imageUrl ? { backgroundImage: imageUrl } : {}}
      >
        {/* biome-ignore lint/correctness/noRestrictedElements: Native anchor required for ABTasty runtime href override */}
        <a
          ref={anchor}
          href={url}
          data-testid='link-container'
          className={classes.linkContainer}
          aria-label={`Navigate to ${title}`}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
        >
          <div className={classes.title}>{title}</div>
          <Button
            onClick={() => {
              anchor.current?.click();
            }}
            isOnDark={isOnDark}
            iconName={IconName.CHEVRON_RIGHT}
            size={ButtonSize.SM}
            variant={variant}
            label={buttonLabel}
            noPrefetchOnHover
          />
        </a>
      </div>
    </div>
  );
};

export default FeatureLink;
